<template>
    <div>
        <div class="session__container" v-if="state.timer">
            <div class="session__timer">
                <img :src="require('@/assets/icons/timer-icon.svg')" alt="info">
                <p>You have <b>{{ sessionDuration }}</b> to complete this payment</p>
            </div>
        </div>
        <div class="payment container__col-sm-12" role="main">
            <div v-if="state.isTestMode" class="testmode__info">
                <img :src="require('@/assets/icons/info-icon.svg')" alt="info">
                <p> {{ $t("ui.testmode.banner") }} </p>
            </div>
            <div class="payment__top">
                <button :class="{ hidden: !adaptStateToDevice(), noshow: !state.hasBack }" class="back-button">
                    <svg width="7" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M6.467.719a.41.41 0 00.126-.3.387.387 0 00-.126-.293A.387.387 0 006.174 0a.41.41 0 00-.3.126L0 6l5.874 5.881c.084.08.184.119.3.119.117 0 .214-.04.293-.119a.41.41 0 00.126-.3.387.387 0 00-.126-.293L1.187 6 6.466.719z"
                            fill="#2D9CDB"
                        />
                    </svg>
                    <p @click="goBack">{{ translate("ui.back.btn", "Go back")}}</p>
                </button>

                <locale-switcher 
                    v-if="adaptStateToDevice()"
                    @toggle-locale="handleLocaleChange"
                />
            </div>
            <mobile-payment-header
                :customer="state.customer"
                :title="state.method.label"
                :method="currentPaymentOption"
            />
            <div class="payment-header__wrapper">
                <payment-header
                    :merchant="state.merchant"
                    :customer="state.customer"
                    :title="state.method.label"
                    :amount="currentPaymentOption === 'card' && state.dcc ? state.dcc.charge_amount : amount"
                    :currency="currentPaymentOption === 'card' && state.dcc ? state.dcc.to : currency"
                    :method="currentPaymentOption"
                />
            </div>
            <Loader />
            <payment-noop
                v-if="state.currentView == 'NOOP_VIEW' && state.processing != true"
            />
            <payment-body v-else :current-payment-option="currentPaymentOption" />
            <secured-by-flutterwave />
        </div>
    </div>
</template>
<script>
import { ref, onBeforeUnmount, onMounted, computed } from "vue";
import { injectStore, mapState } from "redux-vuex";
import { getValueFromPath } from "@/support/helpers";
import PaymentHeader from "@/components/layouts/payment-header";
import PaymentBody from "@/components/layouts/payment-body";
import MobilePaymentHeader from "@/components/layouts/mobile-payments/mobile-payment-header";
import SecuredByFlutterwave from "@/components/secured-by-flutterwave";
import PaymentNoop from "@/components/common/payment-noop";
import LocaleSwitcher from "@/components/locale-switcher.vue";

export default {
    name: "Payment",
    components: {
        PaymentBody,
        PaymentNoop,
        MobilePaymentHeader,
        PaymentHeader,
        SecuredByFlutterwave,
        LocaleSwitcher,
    },
    props: {
        listOfOptions: {
            type: Array,
            required: true,
        },
        options: {
            type: Array,
            required: true,
        },
        currentPaymentOption: {
            type: String,
            required: true,
        },
        amount: {
            type: String,
            required: true,
        },
        currency: {
            type: String,
            required: true,
        },
    },
    methods: {},
    data() {
        return {};
    },
    setup() {
        const store = injectStore();
        const checkoutData = ref(store.getState());
        const subscription = store.subscribe(() => {
            checkoutData.value = store.getState();
        });
        const customDuration = ref(
            getValueFromPath(
                checkoutData.value,
                "session.session_duration",
            ) || null,
        );
        const timingDown = ref(null);

        onBeforeUnmount(() => {
            subscription();
        });
        onMounted(() => {
            customDuration.value = customDuration.value * 60;
            timingDown.value = setInterval(() => handleCountdown(), 1000);
        });
        const sessionDuration = computed(() => {
            let duration = customDuration.value;
            let hours = Math.floor(duration / 3600);
            let minutes = Math.floor((duration % 3600) / 60);
            let seconds = duration % 60;
            if (hours) {
                return hours.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0") + ":" + seconds.toString().padStart(2, "0");
            }
            else return minutes.toString().padStart(2, "0") + ":" + seconds.toString().padStart(2, "0");
        });

        function handleCountdown() {
            if (customDuration.value > 0) {
                customDuration.value--;
                return;
            }
            clearInterval(timingDown.value);
        }

        function goBack() {
            checkoutData.value.view.context.$actions.back();
        }
        function handleCloseIframe(state) {
            store.Control.close(state);
        }
        function handleLocaleChange(locale) {
            store.CurrentLocale.switch(locale);
        }
        return {
            state: mapState({
                back: (state) => getValueFromPath(state, "view.current") || null,
                currentView: (state) => getValueFromPath(state, "view.current") || null,
                dcc: (state) => getValueFromPath(state, "dcc") || null,
                method: (state) => getValueFromPath(state, "method") || [],
                customer: (state) => getValueFromPath(state, "customer") || null,
                merchant: (state) => getValueFromPath(state, "merchant") || null,
                processing: (state) => getValueFromPath(state, "processing") || false,
                hasBack: (state) => getValueFromPath(state, "view.context.hasBackAction") || false,
                isTestMode: (state) => getValueFromPath(state, "isTestMode") || false,
                timer: (state) => getValueFromPath(state, "session.session_duration") || null,
            }),
            handleCloseIframe,
            handleLocaleChange,
            goBack,
            sessionDuration,
        };
    },
    mounted() {},
};
</script>
<style lang="scss" scoped>
.payment {
  width: 100%;
  position: relative;
  @media screen and (min-width: $breakpoint-middle * 1px) {
    max-width: 390px;
    margin-left: auto;
    margin-right: 5vw;
    padding-top: 15vh;
  }
  @media screen and (min-width: $breakpoint-large * 1px) {
    margin-right: 10vw;
  }

  &-header__wrapper {
    margin: 0rem 1rem;
    @media screen and (max-width: $breakpoint-middle * 1px) {
      margin: 0;
      padding-left: 2.4rem;
    }
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media screen and (min-width: $breakpoint-middle * 1px) {
        margin-bottom: 2.8rem;
    }
  }
}
.back-button {
  font-size: 1.3rem;
  line-height: 1.5rem;
  display: flex;
  align-items: center;
  color: $blue;
  border: none;
  background: transparent;
  p {
    margin-left: 0.741rem;
  }
}
.noshow{
    visibility: hidden;
}

.testmode__info {
    background-color: #FFF6E9;
    font-size: 14px;
    padding: 12px 24px;
    display: flex;
    align-items: center;
    gap: 10px;

    img {
        flex: 0 1 15%;
        height: auto;
    }
}

.session__container {
    background-color: #FFF6E9;
    padding: 17px 0px;
}
.session__timer {
    width: 100%;
    font-size: 12px;
    padding: 0px 24px;
    display: flex;
    align-items: center;
    gap: 10px;

    img {
        width: 21px;
        height: 21px;
    }

    @media screen and (min-width: $breakpoint-middle * 1px) {
        max-width: 390px;
        margin-left: auto;
        margin-right: 5vw;
        padding: 0;
    }
    @media screen and (min-width: $breakpoint-large * 1px) {
        margin-right: 10vw;
        padding: 0;
    }
}
</style>